import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { CheckCircle2, Hourglass, Sparkles } from 'lucide-react';

/**
 * AdminPerceptionStatusPill
 * Compact status indicator showing whether a subscriber has completed the
 * ReGenflō Shift onboarding. Read-only, admin/care-team facing.
 *
 * Renders nothing while loading, or a small pill with status + completion
 * date + (on hover) selected reflections + pledge state.
 */
export default function AdminPerceptionStatusPill({ subscriberId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subscriberId) { setLoading(false); return; }
    let mounted = true;
    axios.get(`${API}/perception-onboarding/admin/${subscriberId}`)
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [subscriberId]);

  if (loading || !data) return null;

  const status = data.status || 'not_started';
  const completed = status === 'completed';
  const inProgress = status === 'in_progress';
  const reminded = status === 'remind_later';

  const styleMap = {
    completed:    { bg: 'bg-emerald-500/15', fg: 'text-emerald-300', label: 'Perception Shift • Completed', icon: CheckCircle2 },
    in_progress:  { bg: 'bg-amber-500/15',   fg: 'text-amber-300',   label: 'Perception Shift • In progress', icon: Sparkles },
    remind_later: { bg: 'bg-sky-500/15',     fg: 'text-sky-300',     label: 'Perception Shift • Reminded later', icon: Hourglass },
    not_started:  { bg: 'bg-slate-500/15',  fg: 'text-slate-400',   label: 'Perception Shift • Not started', icon: Hourglass },
  };
  const s = styleMap[status] || styleMap.not_started;
  const Icon = s.icon;

  const reflections = data.selected_reflections || {};
  const oldModel = (reflections.old_model_patterns || []).join(', ');
  const focus = (reflections.priority_focus || []).join(', ');
  const completedAt = completed && data.completed_at
    ? new Date(data.completed_at).toLocaleDateString()
    : null;

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${s.bg} ${s.fg} text-[10px] font-medium`}
        data-testid={`perception-pill-${subscriberId}`}
        title={[
          s.label,
          completedAt ? `on ${completedAt}` : null,
          oldModel ? `Old model: ${oldModel}` : null,
          focus ? `Focus: ${focus}` : null,
          completed ? `Pledge accepted: ${data.pledge_accepted ? 'yes' : 'no'}` : null,
        ].filter(Boolean).join(' • ')}
      >
        <Icon className="h-3 w-3" />
        {completed ? 'Participant' : inProgress ? 'Shift in progress' : reminded ? 'Reminded' : 'Not started'}
      </span>
    );
  }

  return (
    <div
      className={`rounded-md border border-white/[0.06] ${s.bg} px-3 py-2`}
      data-testid={`perception-status-${subscriberId}`}
    >
      <div className={`flex items-center gap-2 ${s.fg}`}>
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[11px] font-medium">{s.label}</span>
        {completedAt ? (
          <span className="ml-auto text-[10px] text-white/55">{completedAt}</span>
        ) : null}
      </div>
      {(oldModel || focus) ? (
        <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px] text-white/65">
          {oldModel ? <div><span className="text-white/40">Old model:</span> {oldModel}</div> : null}
          {focus ? <div><span className="text-white/40">Focus:</span> {focus}</div> : null}
        </div>
      ) : null}
      {completed ? (
        <div className="mt-1 text-[10px] text-white/45">
          Pledge accepted: {data.pledge_accepted ? 'Yes' : 'No'}
        </div>
      ) : null}
    </div>
  );
}
